import {combineLatest} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {timeUtil} from '@/helper/time';
import {numUtil} from '@/helper/num';
import {orderUtil} from '@/helper/order';
import {getQtyTypePriceByOrderType, qtyUtil} from '@/helper/qty-type-contract';
import {calcValue} from '@/calc';
import {contractsCache$} from '@/config';
import {isTrailingStopOrder} from '@/tpslts/order-filters';
import {contractActiveOrdersCache$, contractActiveOrdersCacheBO$} from './orders-active-contract-cache';
import type {ws, ContractActiveOrder} from '@/types';
type ContractOrderRaw = ws.ContractOrderRaw;

const activeOrderMap$ = combineLatest([contractActiveOrdersCache$, contractsCache$]).pipe(
  map(([cache, contractsMap]) => cache.toObject((o) => convertToActiveOrder(o, contractsMap))),
  startWith({})
);
const activeBracketOrderMap$ = combineLatest([contractActiveOrdersCacheBO$, contractsCache$]).pipe(
  map(([cacheBO, contractsMap]) => {
    const orders = convertBracketOrders(cacheBO, contractsMap);
    return orders.toObject('orderID');
  }),
  startWith({})
);

export const contractActiveOrders$ = combineLatest([activeOrderMap$, activeBracketOrderMap$]).pipe(
  map<any, ContractActiveOrder[]>(([activeOrderMap, activeBracketOrderMap]) =>
    Object.values(Object.assign({}, activeOrderMap, activeBracketOrderMap))
  )
);

function calcPrice(raw, pricePrecision, priceScale) {
  const isMarket = orderUtil.isMarketOrder(raw.ordType);
  if (isMarket) {
    return 'Market';
  }
  if (raw.priceEp === 0) {
    return '-';
  }
  return numUtil.floor(raw.priceEp, pricePrecision, priceScale);
}

function calcFillPrice(raw, contractSide, valueFactor, contractSize, precision) {
  if (raw.cumValueEv === 0 || raw.cumQty === 0) {
    return '-';
  }
  const fillPrice = numUtil.floor(
    Math.pow(raw.cumValueEv / (raw.cumQty * valueFactor * contractSize), contractSide),
    precision
  );
  return numUtil.delimit(fillPrice);
}

function calcStopPx(ordType, stopPxEp, pricePrecision, priceScale) {
  if (orderUtil.isConditionalOrder(ordType)) {
    return stopPxEp === 0 ? '-' : numUtil.floor(stopPxEp, pricePrecision, priceScale);
  }

  return '-';
}

function convertToActiveOrder(
  raw: ContractOrderRaw,
  contractsMap,
  primaryOrderPrice?
): ContractActiveOrder & {
  displayQty: string;
  orderQtyByCont: string;
  orderQtyByValue: string;
  orderQtyByCoin: string;
  cumQtyByCont: string;
  cumQtyByValue: string;
  cumQtyByCoin: string;
  leavesQtyByCont: string;
  leavesQtyByValue: string;
  leavesQtyByCoin: string;
  posSide: string;
  icebergOrderQtyByCont: string;
  icebergOrderQtyByValue: string;
  icebergOrderQtyByCoin: string;
} {
  const contract = contractsMap.get(raw.symbol);
  const {
    displaySymbol,
    settleCurrencyDisplay,
    contractSide,
    contractSize,
    valuePrecision,
    valueFactor,
    pricePrecision,
    priceFactor,
    priceScale,
  } = contract;
  const {
    orderID,
    clOrdID,
    ordType,
    ordStatus,
    // @ts-ignore
    code,
    symbol,
    side,
    orderQty,
    // @ts-ignore
    cumQty,
    // @ts-ignore
    leavesQty,
    priceEp,
    // @ts-ignore
    stopPxEp,
    // @ts-ignore
    takeProfitEp,
    // @ts-ignore
    stopLossEp,
    // @ts-ignore
    tpTrigger,
    // @ts-ignore
    slTrigger,
    // @ts-ignore
    execInst,
    // @ts-ignore
    actionTimeNs = 0,
    // @ts-ignore
    timeInForce = '',
    // @ts-ignore
    curPosTerm,
    posSide,
    // @ts-ignore
    tpPxRp,
    // @ts-ignore
    slPxRp,
    // @ts-ignore
    slTimeInForce,
    // @ts-ignore
    tpTimeInForce,
    // @ts-ignore
    displayQty,
    // @ts-ignore
    pegPriceType,
    orderListStatus,
    orderListType,
    orderListId,
  } = raw;
  const value = calcValue(contractSide, contractSize, priceFactor, valuePrecision, orderQty, priceEp);
  const actionTimestamp = Math.floor(actionTimeNs / 1e6);
  const orderQtyStr = orderUtil.formatOrderQty(orderQty);

  const price = calcPrice(raw, pricePrecision, priceScale);
  const qtyPrice =
    primaryOrderPrice || numUtil.floor(getQtyTypePriceByOrderType(raw, ordType), pricePrecision, priceScale);

  return {
    orderID,
    clOrdID,
    ordType,
    displayOrdType: isTrailingStopOrder(pegPriceType, ordType) ? 'TrailingStop' : ordType,
    ordStatus,
    code,
    symbol,
    displaySymbol,
    side,
    displayQty,
    orderQty: Number(orderQty),
    orderQtyByCont: qtyUtil.toCont(contract, orderQty),
    orderQtyByValue: qtyUtil.toValue(contract, orderQty, qtyPrice),
    orderQtyByCoin: qtyUtil.toCoin(contract, orderQty, qtyPrice),
    orderQtyStr,
    cumQty,
    cumQtyByCont: qtyUtil.toCont(contract, cumQty),
    cumQtyByValue: qtyUtil.toValue(contract, cumQty, qtyPrice),
    cumQtyByCoin: qtyUtil.toCoin(contract, cumQty, qtyPrice),
    leavesQty,
    leavesQtyByCont: qtyUtil.toCont(contract, leavesQty),
    leavesQtyByValue: qtyUtil.toValue(contract, leavesQty, qtyPrice),
    leavesQtyByCoin: qtyUtil.toCoin(contract, leavesQty, qtyPrice),
    priceEp: Number(priceEp),
    price: numUtil.delimit(price),
    stopPxEp,
    stopPx: calcStopPx(ordType, stopPxEp, pricePrecision, priceScale),
    fillPrice: calcFillPrice(raw, contractSide, valueFactor, contractSize, pricePrecision),
    value,
    valueWU: value + ' ' + settleCurrencyDisplay,
    takeProfitEp,
    takeProfitPrice: numUtil.delimit(numUtil.floor(takeProfitEp, pricePrecision, priceScale)),
    stopLossEp,
    stopLossPrice: numUtil.delimit(numUtil.floor(stopLossEp, pricePrecision, priceScale)),
    tpTrigger,
    slTrigger,
    execInst,
    actionTimestamp,
    actionTime: timeUtil.formatDateTime(actionTimestamp),
    actionMonthDayTime: timeUtil.formatMonthDayTime(actionTimestamp),
    timeInForce,
    removable: orderUtil.isFinalOrder(ordStatus),
    curPosTerm,
    posSide,
    tpPxRp,
    slPxRp,
    slTimeInForce,
    tpTimeInForce,
    icebergOrderQtyByCont: qtyUtil.toCont(contract, displayQty ?? 0),
    icebergOrderQtyByValue: qtyUtil.toValue(contract, displayQty ?? 0, qtyPrice),
    icebergOrderQtyByCoin: qtyUtil.toCoin(contract, displayQty ?? 0, qtyPrice),
    orderListStatus,
    orderListType,
    orderListId,
  };
}

function convertBracketOrders(cacheBO, contractsMap) {
  const items = cacheBO.toArray();
  const primaryOrders = items.filter((o) => o.ordType === 'Bracket');
  return primaryOrders.map((o) => {
    const po = convertToActiveOrder(o, contractsMap);
    const {tpOrder, slOrder} = findTpSlOrders(items, po);
    if (tpOrder && slOrder) {
      po.tpOrder = convertToActiveOrder(tpOrder, contractsMap, po.price);
      po.slOrder = convertToActiveOrder(slOrder, contractsMap, po.price);
      po.removable =
        orderUtil.isFinalOrder(po.ordStatus) &&
        orderUtil.isFinalOrder(tpOrder.ordStatus) &&
        orderUtil.isFinalOrder(slOrder.ordStatus);
    }
    return po;
  });
}

function findTpSlOrders(orders, po) {
  return {
    tpOrder: orders.find((o) => o.clOrdID === po.orderID + '_TP'),
    slOrder: orders.find((o) => o.clOrdID === po.orderID + '_SL'),
  };
}
